import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useUser } from '@client/hooks/business/user/useUser';
import { useHasRouteAccess } from '@client/hooks/navigation/useHasRouteAccess';
import { getHomeRoleRedirectTarget } from '@client/routes/~console.$orgId/home-role-redirect';
import { useLocation } from '@tanstack/react-router';
import { Result } from 'antd';
import { useId } from 'react';

function focusHeading(heading: HTMLHeadingElement | null) {
    heading?.focus();
}

/**
 * "Back to home" opens the console Home route, which forwards a user without a Home role to
 * their own landing. When that landing is itself closed to the signed-in user the action would
 * only return to this page, so it is left out; the shell's navigation and account menu remain.
 */
function useCanOpenConsoleHome() {
    const user = useUser({ throwOnUndefined: false });
    const hasRouteAccess = useHasRouteAccess();

    return !user || hasRouteAccess(getHomeRoleRedirectTarget(user.roles) ?? '/console/$orgId');
}

export function NotFound() {
    const orgId = useOrgId();
    const { pathname } = useLocation();
    const descriptionId = useId();
    const canOpenConsoleHome = useCanOpenConsoleHome();
    const isConsolePath = pathname === '/console' || pathname.startsWith('/console/');
    const isAdminConsolePath = pathname === '/admin-console' || pathname.startsWith('/admin-console/');

    return (
        // Keep this in document flow: a mounted shell must remain visible and keyboard usable.
        <div className="grid min-h-[70dvh] w-full min-w-0 place-items-center bg-background px-4 py-8">
            <Result
                status="404"
                title={
                    // A new URL gets entry focus; the stable ref leaves ordinary re-renders alone.
                    // curium:allow-heading — 404 page shown in place of the route, so no route title applies
                    <h1
                        key={pathname}
                        ref={focusHeading}
                        tabIndex={-1}
                        aria-describedby={descriptionId}
                        className="m-0 inline-block rounded-sm font-normal text-2xl text-foreground focus-visible:outline-2 focus-visible:outline-accent-500 focus-visible:outline-offset-4"
                    >
                        404
                    </h1>
                }
                subTitle={
                    <span id={descriptionId} className="text-muted-foreground">
                        Sorry, the page you visited does not exist.
                    </span>
                }
                extra={
                    isConsolePath && orgId > 0n ? (
                        canOpenConsoleHome && (
                            <Button.Link to="/console/$orgId" params={{ orgId }} variant="secondary">
                                Back to home
                            </Button.Link>
                        )
                    ) : isAdminConsolePath ? (
                        <Button.Link to="/admin-console" variant="secondary">
                            Back to home
                        </Button.Link>
                    ) : (
                        <Button.Link to="/" variant="secondary">
                            Back to home
                        </Button.Link>
                    )
                }
                className="mx-auto w-full min-w-0 max-w-lg rounded-lg border border-separator bg-card p-4 sm:p-8 [&_.ant-result-image]:max-w-full [&_.ant-result-image_svg]:h-auto [&_.ant-result-image_svg]:w-full"
            />
        </div>
    );
}
